import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar } from 'recharts';
import { useEmpresaContext } from '../../contexts/EmpresaContext';
import { useAuthSimple } from '../../hooks/useAuth';
import { dashboardService } from '../../services/dashboardService';

const COLORS = ['#90CAF9', '#A5D6A7', '#FFE082', '#FFAB91', '#B39DDB'];

interface Props { year?: string; month?: string }
export const DashboardCharts: React.FC<Props> = ({ year, month }) => {
  const { empresaId, isUserFromEmpresa } = useEmpresaContext();
  const { user, isGrupo, isEmpresa } = useAuthSimple();
  const [producao, setProducao] = useState<any[]>([]);
  const [comissoes, setComissoes] = useState<any[]>([]);
  const [porSeguradora, setPorSeguradora] = useState<any[]>([]);
  const [topProdutos, setTopProdutos] = useState<any[]>([]);
  const [showAllSeguradoras, setShowAllSeguradoras] = useState(false);
  const [showAllProdutos, setShowAllProdutos] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // Build params with year and month when available
        const params: any = {};
        if (year && year !== '') params.year = Number(year);
        if (month && month !== '') params.month = Number(month);
        
        let res;
        
        // Handle different user types
        if (isGrupo && user?.grupoId) {
          res = await dashboardService.grupoOverview(user.grupoId, params);
        } else if ((isEmpresa || isUserFromEmpresa) && empresaId) {
          res = await dashboardService.empresaOverview(empresaId, params);
        } else {
          return;
        }
        
        const payload = res?.data ?? res;

        // Try multiple possible data paths for production and commission data
        const producaoData = payload?.graficos?.producaoMensal ?? 
                           payload?.producaoMensal ?? 
                           payload?.graficos?.producao ?? 
                           payload?.producao ?? [];
        
        const comissoesData = payload?.graficos?.comissaoMensal ?? 
                            payload?.comissaoMensal ?? 
                            payload?.graficos?.comissoes ?? 
                            payload?.comissoes ?? 
                            payload?.graficos?.comissao ?? 
                            payload?.comissao ?? [];

        setProducao(producaoData);
        setComissoes(comissoesData);
        
        // For seguradoras and produtos, use monthly params if we need monthly data
        // But for now, let's use the same response
        const seguradorasData = (payload?.graficos?.documentosPorSeguradora ?? [])
          .map((item: any) => ({
            nome: item?.nome || item?.seguradora?.nome || 'N/A',
            quantidade: item?.quantidade || item?.totalDocumentos || item?.total || 0
          }))
          .sort((a: any, b: any) => b.quantidade - a.quantidade); // Sort by quantity descending
          
        setPorSeguradora(seguradorasData);
        
        const produtosData = (payload?.graficos?.topProdutos ?? [])
          .map((item: any) => ({
            nome: item?.nome || item?.produto?.nome || 'N/A',
            quantidade: item?.quantidade || item?.totalPremios || item?.total || 0
          }))
          .sort((a: any, b: any) => b.quantidade - a.quantidade); // Sort by quantity descending
        setTopProdutos(produtosData);
      } catch (err) {
        console.error('Erro ao carregar gráficos do dashboard:', err);
        setProducao([]);
        setComissoes([]);
        setPorSeguradora([]);
        setTopProdutos([]);
      }
    };

    load();
  }, [empresaId, isUserFromEmpresa, isGrupo, isEmpresa, user?.grupoId, user?.type, month, year]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" key={`charts-${year}-${month}`}>
      <div className="p-4 rounded shadow" style={{ backgroundColor: '#FFFFFF' }}>
        <h4 className="text-sm font-semibold mb-2">Produção Mensal (Prêmio Líquido) {year && `- ${year}`}</h4>
        <div style={{ width: '100%', height: 260 }}>
          {producao.length > 0 ? (
            <ResponsiveContainer>
              <LineChart data={producao} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip formatter={(value) => [`R$ ${Number(value).toLocaleString('pt-BR')}`, 'Prêmio Líquido']} />
                <Line type="monotone" dataKey="premioLiquido" stroke="#7986CB" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="valor" stroke="#7986CB" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="total" stroke="#7986CB" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p>Nenhum dado disponível para o período selecionado</p>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 rounded shadow" style={{ backgroundColor: '#FFFFFF' }}>
        <h4 className="text-sm font-semibold mb-2">Comissões por Mês {year && `- ${year}`}</h4>
        <div style={{ width: '100%', height: 260 }}>
          {comissoes.length > 0 ? (
            <ResponsiveContainer>
              <LineChart data={comissoes} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis />
                <Tooltip formatter={(value) => [`R$ ${Number(value).toLocaleString('pt-BR')}`, 'Comissão']} />
                <Line type="monotone" dataKey="comissao" stroke="#81C784" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="valor" stroke="#81C784" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="total" stroke="#81C784" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p>Nenhum dado disponível para o período selecionado</p>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 rounded shadow md:col-span-2" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold">📊 Documentos por Seguradora</h4>
          <button
            onClick={() => setShowAllSeguradoras(!showAllSeguradoras)}
            className="px-3 py-1 text-xs rounded-md transition-colors" style={{ backgroundColor: '#E8EAF6', color: '#5C6BC0' }}
          >
            {showAllSeguradoras ? `Mostrar Top 5` : `Ver todas (${porSeguradora.length})`}
          </button>
        </div>
        
        {porSeguradora.length > 0 ? (
          <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
            <div className="space-y-2 font-mono text-sm">
              {(showAllSeguradoras ? porSeguradora : porSeguradora.slice(0, 5)).map((item) => {
                const maxQuantidade = porSeguradora[0]?.quantidade || 1;
                const barLength = Math.round((item.quantidade / maxQuantidade) * 40);
                const progressBar = '█'.repeat(barLength);
                const companyName = item.nome.length > 45 ? `${item.nome.substring(0, 45)}...` : item.nome;
                
                return (
                  <div key={item.nome} className="flex items-center py-1" title={item.nome}>
                    <span className="w-96 text-left text-gray-700 truncate">
                      {companyName}
                    </span>
                    <span className="mx-3" style={{ color: '#7986CB' }}>
                      {progressBar}
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {item.quantidade}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-40 text-gray-500">
            <p>Nenhum dado disponível para o período selecionado</p>
          </div>
        )}
      </div>

      <div className="p-4 rounded shadow md:col-span-2" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold">📦 Top Produtos</h4>
          <button
            onClick={() => setShowAllProdutos(!showAllProdutos)}
            className="px-3 py-1 text-xs rounded-md transition-colors" style={{ backgroundColor: '#E8F5E9', color: '#388E3C' }}
          >
            {showAllProdutos ? `Mostrar Top 5` : `Ver todos (${topProdutos.length})`}
          </button>
        </div>
        
        {topProdutos.length > 0 ? (
          <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
            <div className="space-y-2 font-mono text-sm">
              {(showAllProdutos ? topProdutos : topProdutos.slice(0, 5)).map((item) => {
                const maxQuantidade = topProdutos[0]?.quantidade || 1;
                const barLength = Math.round((item.quantidade / maxQuantidade) * 40);
                const progressBar = '█'.repeat(barLength);
                const productName = item.nome.length > 45 ? `${item.nome.substring(0, 45)}...` : item.nome;
                
                return (
                  <div key={item.nome} className="flex items-center py-1" title={item.nome}>
                    <span className="w-96 text-left text-gray-700 truncate">
                      {productName}
                    </span>
                    <span className="mx-3" style={{ color: '#81C784' }}>
                      {progressBar}
                    </span>
                    <span className="text-gray-900 font-semibold">
                      {item.quantidade}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-40 text-gray-500">
            <p>Nenhum dado disponível para o período selecionado</p>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default DashboardCharts;
